// Character save-slot CRUD on top of the Renavora IndexedDB.
// Phase 3 Step 5 (2/4) — initial v1.
// Phase 3 Step 9 (1/4) — bumped to v2 (added `inventory`).
// Phase 3 Step 10a-1 — bumped to v3 (added `currentRun` for dungeon runs).
// Phase 3 Step 10b-2 — bumped to v4 (added `currentRunNodeCleared` so the
// UI can decide whether to route into the battle view; intentionally a
// client-only flag, not in shared/DungeonRunState — see plan §2.6).
// Phase 3 Step 10c-phase4-prep — bumped to v5 (added `gold` and `xp` so
// boss-win rewards from `computeRunReward` can persist on the character
// record; 10d-summary previously left them display-only). Existing v4
// rows are migrated lazily by injecting `gold: 0` / `xp: 0`.
// Phase 5 Step 5-5 — bumped to v6 (added `borrowedAllies` for frozen
// Phase 5 snapshot party slots). Existing rows migrate to an empty party.
// Phase 5.x-A1 — bumped to v7 (removed deprecated aggregate `level` field per
// ADR 0004 §2.2). Existing rows are hydrated by ignoring that legacy field.
// Phase 5.x-A3 — bumped to v8 (reinterpreted `xp` as current-job XP feeding
// `jobLevel`; legacy unspent cross-job XP is reset during hydrate).
// Phase 5.x-C1 — bumped to v9 (added `completedDungeons` for Tier progression
// gates). Existing rows migrate to an empty clear history.
// Phase 5.x-W ④-b — bumped to v10 (replaced one-way `borrowedAllies` with mutual
// `partyLinks`). A one-way borrowed party cannot be converted to mutual links
// (mutual requires both-sided consent), so existing rows discard `borrowedAllies`
// and start with an empty `partyLinks` party.
//
// JobDefinition is NOT serialized directly. We store `jobId: string` and
// rehydrate it through @renavora/content's JOBS_BY_ID at load time. The
// rehydrate step itself is the consumer's responsibility (Step 7+).

import type { DungeonRunState, DungeonTemplateId, Item, PartyLink } from '@renavora/shared';

import { openDb } from './db.js';

/** Bump when the record payload shape changes (independent of DB version). */
export const SCHEMA_VERSION = 10 as const;

/**
 * Schema boundary where `xp` became current-job XP. Earlier schemas used the
 * field as an unspent cross-job reward balance and must reset it on hydrate;
 * v8+ records must preserve it across future schema bumps.
 */
export const CURRENT_JOB_XP_SCHEMA_VERSION = 8 as const;

/**
 * Dehydrated character ready for serialization. CharacterSetup carries a
 * full JobDefinition by value, but we keep references by id so save slots
 * stay valid across content catalogue revisions.
 */
export interface SerializableCharacter {
  /** Resolves through JOBS_BY_ID at hydrate time (Step 7+). */
  jobId: string;
  jobLevel: number;
  jobHistory: ReadonlyArray<{ jobId: string; maxLv: number }>;
  /** @renavora/shared Item is pure data and JSON-serializable as-is. */
  equipment: ReadonlyArray<Item>;
  /** Unequipped items owned by the character. Added in v2 (Step 9). */
  inventory: ReadonlyArray<Item>;
  /**
   * In-progress dungeon run, or null when not in a dungeon. Added in v3
   * (Step 10a-1). The DungeonMap itself is not persisted — `mapId` carries
   * `${templateId}#${seed}` and consumers rebuild the map deterministically
   * via `generateDungeonMap`.
   */
  currentRun: DungeonRunState | null;
  /**
   * Whether the currently-occupied node has been resolved (battle won,
   * camp consumed, etc.). `null` when no run is in progress. Added in v4
   * (Step 10b-2) so the dungeon-page can route into a battle view exactly
   * when a battle/elite node is unresolved. Start nodes are always
   * cleared; advanceDungeon flips this to `false` when stepping onto a
   * battle/elite node; `completeCurrentNode` flips it back to `true`.
   *
   * Kept here rather than on the shared `DungeonRunState` because it is
   * purely a UI-layer scheduling concern (see plan §2.6).
   */
  currentRunNodeCleared: boolean | null;
  /**
   * Lifetime gold balance. Added in v5 (Step 10c-phase4-prep). Boss wins
   * deposit `RunReward.goldEarned` here; future shops / 10c-event
   * `gold-delta` will both credit and debit. Subsystems consuming gold
   * (shop pricing, 10c-event balance) live in Phase 4; this field is just
   * the storage.
   */
  gold: number;
  /**
   * Current-job XP balance. Added in v5 (Step 10c-phase4-prep) and wired to
   * the current `jobLevel` in Phase 5.x-A3. Boss wins deposit
   * `RunReward.xpEarned`; job changes reset this to the restarted jobLevel's
   * XP floor so ADR 0004's per-current-job level remains the combat source.
   */
  xp: number;
  /**
   * Dungeon template ids cleared at least once. Added in v9 for progression
   * gates; order follows first-clear chronology and ids are unique.
   */
  completedDungeons: ReadonlyArray<DungeonTemplateId>;
  /**
   * Mutual-party links (ADR 0038 §2.2). Each entry holds the *partner's* frozen
   * snapshot; at run start they convert to in-battle `BorrowedAlly`s. Replaced
   * the one-way `borrowedAllies` field in v10; the in-battle ally representation
   * (`DungeonRunState.borrowedAllies`) is unchanged.
   */
  partyLinks: ReadonlyArray<PartyLink>;
}

/** One row in the `characters` object store. */
export interface SerializedCharacter {
  id: string;
  name: string;
  schemaVersion: typeof SCHEMA_VERSION;
  createdAt: number;
  updatedAt: number;
  character: SerializableCharacter;
}

/**
 * Default jobId for new save slots. Locked by docs/adr/0005-c2-job-complete-list.md
 * and re-verified at CI time by character.test.ts → "default character jobId
 * is present in JOBS_BY_ID". Kept exported so the test can read it without
 * duplicating the string literal.
 *
 * The runtime catalogue check that would normally live inside
 * newDefaultCharacter is intentionally NOT done here — pulling
 * @renavora/content into production code added ~140 kB raw / ~30 kB gzip
 * to the bundle (violates plan §2.3 +15 kB threshold). The test-time
 * assertion gives us the same drift detection without the cost.
 */
export const DEFAULT_JOB_ID = 'warrior';

/**
 * Make a fresh save record using a job Lv1 Warrior. Catalogue alignment for
 * DEFAULT_JOB_ID is verified by character.test.ts, not at runtime — see
 * DEFAULT_JOB_ID's docblock for the rationale.
 */
export function newDefaultCharacter(name: string): SerializedCharacter {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    name,
    schemaVersion: SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    character: {
      jobId: DEFAULT_JOB_ID,
      jobLevel: 1,
      jobHistory: [],
      equipment: [],
      inventory: [],
      currentRun: null,
      currentRunNodeCleared: null,
      gold: 0,
      xp: 0,
      completedDungeons: [],
      partyLinks: [],
    },
  };
}

/**
 * Normalise a raw record read from IndexedDB to the latest schema version.
 * Pure, no I/O. Lazy migration:
 *   v1 → v2: inject `inventory: []`
 *   v2 → v3: inject `currentRun: null`
 *   v3 → v4: inject `currentRunNodeCleared`. If the record carries an
 *            in-progress run, default to `true` (safe — old UI treated
 *            every node as walk-through, never as locked in combat).
 *            With no run, default to `null`.
 *   v4 → v5: inject `gold: 0` / `xp: 0`. Previous runs' boss rewards are
 *            not retroactively reconstructed — pre-v5 saves start at 0
 *            and accumulate from the next boss win.
 *   v5 → v6: inject `borrowedAllies: []`; borrowed allies did not exist
 *            before Phase 5 and cannot be reconstructed from local saves.
 *   v6 → v7: drop legacy aggregate `level`; current job Lv is the only
 *            character level persisted after ADR 0004 §2.2.
 *   v7 → v8: reinterpret `xp` as current-job XP used by the `jobLevel`
 *            reducer. Payload shape is unchanged, but legacy XP is reset to
 *            0 because earlier saves tracked unspent cross-job reward balance.
 *   v8 → v9: inject `completedDungeons: []`; historic clears are not
 *            reconstructed, so progression gates start from the next clear.
 *   v9 → v10: drop one-way `borrowedAllies` and inject `partyLinks: []`. The
 *            one-way borrowed party cannot become mutual links, so it is
 *            discarded; the player starts the mutual era with an empty party.
 * `schemaVersion` is bumped to {@link SCHEMA_VERSION}. The DB is not
 * touched here — the next call to {@link saveCharacter} persists the
 * migrated shape.
 *
 * Returns `undefined` if the record is malformed enough that we cannot
 * safely produce a {@link SerializedCharacter} (caller treats this the
 * same as "not found").
 */
export function hydrateRecord(raw: unknown): SerializedCharacter | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const r = raw as Partial<SerializedCharacter> & {
    character?: Partial<SerializableCharacter>;
  };
  if (
    typeof r.id !== 'string' ||
    typeof r.name !== 'string' ||
    typeof r.createdAt !== 'number' ||
    typeof r.updatedAt !== 'number' ||
    r.character === undefined ||
    r.character === null
  ) {
    return undefined;
  }
  const ch = r.character;
  const rawSchemaVersion = typeof r.schemaVersion === 'number' ? r.schemaVersion : 1;
  const inventory = ch.inventory ?? [];
  const currentRun = ch.currentRun ?? null;
  // v3 → v4 migration: existing in-progress runs were authored under the
  // walk-through model, so resume them in the `cleared` state.
  const currentRunNodeCleared =
    ch.currentRunNodeCleared !== undefined
      ? ch.currentRunNodeCleared
      : currentRun === null
        ? null
        : true;
  // v4 → v5 migration: pre-v5 saves had no gold/xp tracking. Default to 0
  // rather than reconstructing past boss wins — see hydrate docblock.
  const gold = typeof ch.gold === 'number' ? ch.gold : 0;
  const rawXp = typeof ch.xp === 'number' ? ch.xp : 0;
  const xp = rawSchemaVersion >= CURRENT_JOB_XP_SCHEMA_VERSION ? rawXp : 0;
  const completedDungeons = Array.isArray(ch.completedDungeons) ? ch.completedDungeons : [];
  // v9 → v10 migration: the one-way `borrowedAllies` field is dropped (it is not
  // read here at all) and `partyLinks` defaults to an empty mutual party. Saves
  // already on the mutual model keep their links.
  const partyLinks = Array.isArray(ch.partyLinks) ? ch.partyLinks : [];
  return {
    id: r.id,
    name: r.name,
    schemaVersion: SCHEMA_VERSION,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    character: {
      jobId: ch.jobId ?? DEFAULT_JOB_ID,
      jobLevel: ch.jobLevel ?? 1,
      jobHistory: ch.jobHistory ?? [],
      equipment: ch.equipment ?? [],
      inventory,
      currentRun,
      currentRunNodeCleared,
      gold,
      xp,
      completedDungeons,
      partyLinks,
    },
  };
}

/**
 * Persist a save record. Does NOT mutate the input; returns the persisted
 * copy with `updatedAt` refreshed to Date.now(). Caller should treat the
 * return value as the new source of truth.
 */
export async function saveCharacter(record: SerializedCharacter): Promise<SerializedCharacter> {
  const updated: SerializedCharacter = { ...record, updatedAt: Date.now() };
  const db = await openDb();
  await db.put('characters', updated);
  return updated;
}

export async function loadCharacter(id: string): Promise<SerializedCharacter | undefined> {
  const db = await openDb();
  const raw = await db.get('characters', id);
  return raw === undefined ? undefined : hydrateRecord(raw);
}

/**
 * All saved characters, sorted by `updatedAt` descending (most recent first).
 * We could iterate the `byUpdatedAt` cursor in reverse, but the slot count
 * is small enough (manual saves) that an in-memory sort after getAll() is
 * simpler and matches what the UI needs.
 *
 * Every row passes through {@link hydrateRecord}, so legacy v1 records get
 * `inventory: []` injected before they reach the UI.
 */
export async function listCharacters(): Promise<readonly SerializedCharacter[]> {
  const db = await openDb();
  const all = await db.getAll('characters');
  const hydrated: SerializedCharacter[] = [];
  for (const raw of all) {
    const rec = hydrateRecord(raw);
    if (rec !== undefined) hydrated.push(rec);
  }
  return hydrated.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteCharacter(id: string): Promise<void> {
  const db = await openDb();
  await db.delete('characters', id);
}
